import moment from 'moment-timezone'
import * as React from 'react'
import { Query, QueryResult } from 'react-apollo'
import { TFunction, useTranslation } from 'react-i18next'
import uuid from 'uuid/v1'

import { LOCIZE_NAMESPACE } from 'main/common'
import Loader from 'main/components/common/Loader'
import { title as contactSchoolTitle } from 'main/components/lead/ContactSchoolModal'
import { i18nKeyPrefix } from 'main/components/tour/i18n'
import { CredentialsFields } from 'main/gql/credentials'
import { Modal } from 'main/ui'
import { UnreachableCaseError } from 'main/util/ts'
import AnonymousContent from '../AnonymousContent'
import { track } from '../track'
import { queryVariables, SCHEDULE_TOUR_MODAL_QUERY, ScheduleTourModal as Data } from './common/gql'
import Content from './Content'
import { Step } from './types'

const PRE = `${i18nKeyPrefix}-SCHEDULE-MODAL`

interface IProps {
  schoolId: number
  initialTourOpeningId?: string
  onAuth?: (credentials: CredentialsFields) => void
  onUpdate?: () => void
  onRequestClose: () => void
}

export default function ScheduleTourModal(props: IProps) {
  const { schoolId, initialTourOpeningId, onUpdate, onRequestClose } = props
  const { t } = useTranslation(LOCIZE_NAMESPACE)
  const [step, setStep] = React.useState<Step | null>(null)
  const anchorMoment = moment().add(1, 'day')

  const [sessionId] = React.useState(uuid())
  const trackData = { schoolId, sessionId }
  React.useEffect(() => track('opened', trackData), [])

  return (
    <Query
      query={SCHEDULE_TOUR_MODAL_QUERY}
      variables={queryVariables(schoolId, anchorMoment)}
      onCompleted={(data: Data) => {
        if (data && !step) {
          setStep(initialStep(data))
        } else if (
          !!step &&
          (step.__stepname === 'login' || step.__stepname === 'sign_up') &&
          data &&
          data.user
        ) {
          setStep(initialStep(data))
        }
      }}
    >
      {(result: QueryResult) => {
        const { data, loading, error } = result
        if (loading || error || !data || !step) {
          return <LoadingModal {...props} />
        }

        const { school } = data
        if (!school) {
          throw new Error('no school')
        }

        if (step.__stepname === 'login' || step.__stepname === 'sign_up') {
          if (data.user) {
            return <LoadingModal {...props} />
          }

          return (
            <Modal
              googleOptimize={googleOptimize(step)}
              title={title(step, t)}
              onRequestClose={onRequestClose}
            >
              <AnonymousContent
                trackData={trackData}
                school={school}
                step={step}
                onAuth={credentials => handleAuth(props, credentials)}
                onChangeStep={setStep}
              />
            </Modal>
          )
        }

        const { desiredWeeklySchedule, tourOpenings, upcomingTours, user } = data
        if (!tourOpenings || !upcomingTours || !user) {
          throw new Error('missing data')
        }

        return (
          <Modal title={title(step, t)} onRequestClose={onRequestClose}>
            <Content
              trackData={trackData}
              data={{
                desiredWeeklySchedule,
                school,
                tourOpenings,
                upcomingTours,
                user,
              }}
              anchorMoment={anchorMoment}
              initialTourOpeningId={initialTourOpeningId}
              step={step}
              onUpdate={onUpdate}
              onChangeStep={setStep}
              onRequestClose={handleRequestClose}
            />
          </Modal>
        )
      }}
    </Query>
  )

  function handleRequestClose() {
    track('closed', trackData)
    onRequestClose()
  }
}

function LoadingModal(props: IProps) {
  const { onRequestClose } = props

  return (
    <Modal title="" onRequestClose={onRequestClose}>
      <Loader />
    </Modal>
  )
}

function handleAuth(props: IProps, credentials: CredentialsFields) {
  const { onAuth } = props

  if (onAuth) {
    onAuth(credentials)
  }
}

function initialStep(data: Data): Step {
  const { tourOpenings, upcomingTours, user } = data

  if (upcomingTours && upcomingTours.length) {
    return { __stepname: 'confirmation' }
  }

  if (user) {
    return {
      __stepname: tourOpenings && !tourOpenings.length ? 'not_touring' : 'opening_selection',
    }
  } else {
    return { __stepname: 'sign_up' }
  }
}

function googleOptimize(step: Step) {
  return step.__stepname === 'sign_up' ? { title: 'scheduleTourSignUpTitle' } : {}
}

function title(step: Step | null, t: TFunction) {
  if (!step) return ''

  switch (step.__stepname) {
    case 'login':
    case 'sign_up':
    case 'not_touring':
    case 'opening_selection':
    case 'details':
      return t(`${PRE}-DEFAULT`)

    case 'review':
      return t(`${PRE}-REVIEW`)

    case 'cancel':
      return t(`${PRE}-CANCEL`)

    case 'confirmation':
    case 'confirmation_active':
    case 'cancel_confirmation':
      return ''

    case 'contact':
      return step.contactSchoolStep ? contactSchoolTitle(step.contactSchoolStep, t) : ''

    default:
      throw new UnreachableCaseError(step)
  }
}
